// Uyarlanmış kopya — emsal: beltasapp/frontend/src/shared/ui/kalemler/grid-cell.tsx (GridCellView) + hucre-stil.ts
// Fark: tek `<td>` bileşeni (görünüm + editör seçimi); durum yalnız metin rengi + `data-cell-status`;
// hata mesajı `components/ui/tooltip` ile (etkin hücrede açık kalır); fill/taşıma tutamağı yok.
import { ChevronDown } from 'lucide-react'
import { useState, type MouseEvent } from 'react'

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { messages } from '@/lib/messages'
import { cn } from '@/lib/utils'

import { SelectEditor, TextEditor, type EditorDone } from './cell-editors'
import { cellText } from './plan'
import type { GridColumn } from './types'

const t = messages.editableGrid

export interface GridCellProps<R> {
  id: string
  row: R
  rowKey: string
  rowIndex: number
  colIndex: number
  col: GridColumn<R>
  /** In the selection range. */
  selected: boolean
  /** The active cell (selection anchor). */
  active: boolean
  /** The range spans more than one cell. */
  multi: boolean
  /** Editor open on this cell (`seed` = typed first character). */
  editing: { seed?: string } | null
  editable: boolean
  issue?: string
  flashed: boolean
  onMouseDown: (e: MouseEvent<HTMLElement>) => void
  onMouseEnter: () => void
  onDoubleClick: () => void
  onOpenList: () => void
  onDone: EditorDone
  onPicked: () => void
}

function IssueTip({
  message,
  active,
  children,
}: {
  message: string
  active: boolean
  children: React.ReactNode
}) {
  const [hover, setHover] = useState(false)
  return (
    <Tooltip open={hover || active} onOpenChange={setHover}>
      <TooltipTrigger render={<div className="flex h-full min-w-0 flex-1 items-center" />}>
        {children}
      </TooltipTrigger>
      <TooltipContent side="bottom">{message}</TooltipContent>
    </Tooltip>
  )
}

export function GridCell<R>(p: GridCellProps<R>) {
  const { col, row, active, editing, issue } = p
  const align = col.align ?? (col.kind === 'number' ? 'right' : 'left')
  const descId = issue ? `${p.id}-issue` : undefined
  const mutedReadOnly = col.kind === 'readonly' || !!col.readOnly?.(row)

  let content: React.ReactNode
  if (editing && col.kind === 'select') {
    content = (
      <SelectEditor
        options={col.options?.(row) ?? []}
        current={col.getText(row)}
        seed={editing.seed}
        ariaLabel={col.header}
        onDone={p.onDone}
        onPicked={p.onPicked}
      />
    )
  } else if (editing) {
    content = (
      <TextEditor
        initial={editing.seed ?? col.getText(row)}
        numeric={col.kind === 'number'}
        ariaLabel={col.header}
        onDone={p.onDone}
      />
    )
  } else {
    const display = col.render ? col.render(row) : cellText(col, row)
    const body = (
      <span className={cn('truncate', align === 'right' && 'tabular ml-auto')}>{display}</span>
    )
    content = (
      <>
        {issue ? (
          <IssueTip message={issue} active={active}>
            {body}
          </IssueTip>
        ) : (
          body
        )}
        {col.kind === 'select' && p.editable && (
          // mousedown reaches the cell (selects it); only the button's own focus is prevented.
          <button
            type="button"
            tabIndex={-1}
            aria-label={t.openList}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation()
              p.onOpenList()
            }}
            className={cn(
              'text-muted-foreground hover:text-foreground ml-auto grid size-4 shrink-0 place-items-center',
              !active && 'opacity-0 group-hover/cell:opacity-100',
            )}
          >
            <ChevronDown className="size-3.5" aria-hidden="true" />
          </button>
        )}
        {issue && (
          <span id={descId} className="sr-only">
            {issue}
          </span>
        )}
      </>
    )
  }

  return (
    <td
      id={p.id}
      role="gridcell"
      aria-colindex={p.colIndex + 2}
      aria-rowindex={p.rowIndex + 2}
      aria-selected={p.selected}
      aria-readonly={!p.editable || undefined}
      aria-invalid={issue ? true : undefined}
      aria-describedby={descId}
      data-row-key={p.rowKey}
      data-col-id={col.id}
      data-cell-status={issue ? 'error' : undefined}
      data-cell-flash={p.flashed ? '' : undefined}
      data-cell-active={active ? '' : undefined}
      onMouseDown={p.onMouseDown}
      onMouseEnter={p.onMouseEnter}
      onDoubleClick={p.onDoubleClick}
      className={cn(
        'group/cell border-border-subtle relative h-9 scroll-mt-10 scroll-ml-12 overflow-hidden border-r border-b px-2 text-xs whitespace-nowrap select-none',
        'transition-colors duration-(--duration-considered) motion-reduce:transition-none',
        p.selected && !active && 'bg-brand-subtle',
        active && p.multi && 'bg-background',
        active && 'ring-primary ring-2 ring-inset',
        mutedReadOnly && 'text-muted-foreground',
        'data-[cell-status=error]:text-destructive data-cell-flash:bg-primary/25',
      )}
    >
      <div className={cn('flex h-full items-center gap-1', align === 'right' && 'justify-end')}>
        {content}
      </div>
    </td>
  )
}
